import { Typography } from "@mui/material";
import DocumentCard from "@components/DocumentCard/DocumentCard";
import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { VesselCallMarineInformation as MarineInformation } from "@services/vesselCall/types";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";

const LANG_BASE = "vessel-call.marine-information";
const FIELD_LABEL_CLASS = "w-36 shrink-0 font-normal text-[#646A77]";
const VALUE_CLASS = "min-w-0 flex-1 font-semibold text-[#111827]";

interface MarineInformationProps {
  title: string;
  marineInformation?: MarineInformation | null;
  testId: string;
}

const VesselCallMarineInformation = ({
  title,
  marineInformation,
  testId,
}: MarineInformationProps) => {
  const { t } = useTranslation();
  const { userAccessPermissions, isShippingAgentUser } = useAuthStore();
  const isMarineDepartmentUser = checkPermission(userAccessPermissions, "hasInternalNotesAccess");
  const { handleDownload, handlePreview } = useDownloadCertificate();

  if (!marineInformation || (!isMarineDepartmentUser && !isShippingAgentUser)) return null;

  const hasExternalNotes = Boolean(marineInformation.externalNotes?.trim());
  const hasExternalAttachments = Boolean(marineInformation.externalAttachmentsUrl?.trim());
  const hasInternalNotes =
    isMarineDepartmentUser && Boolean(marineInformation.internalNotes?.trim());
  const hasInternalAttachments =
    isMarineDepartmentUser && Boolean(marineInformation.internalAttachmentsUrl?.trim());
  const hasExternalInformation = hasExternalNotes || hasExternalAttachments;
  const hasInternalInformation = hasInternalNotes || hasInternalAttachments;

  if (!hasExternalInformation && !hasInternalInformation) return null;

  return (
    <div className="flex flex-col gap-4" data-testid={testId}>
      <Paper variant="outlined">
        <Typography
          component="h3"
          variant="subtitle1"
          className="font-bold"
          sx={{ fontWeight: 600, color: "#111827" }}
        >
          {title}
        </Typography>
        <div className="flex flex-col gap-4">
          {hasExternalInformation && (
            <div className="flex flex-col gap-4">
              {hasExternalNotes && (
                <PaperItem
                  testId={`${testId}-external-notes`}
                  translate={t(`${LANG_BASE}.notes-to-agent`)}
                  text={marineInformation.externalNotes}
                  textStyles={VALUE_CLASS}
                  tranStyle={FIELD_LABEL_CLASS}
                  wrapperStyle="min-w-0 gap-4"
                  labelClassName={FIELD_LABEL_CLASS}
                  valueClassName={VALUE_CLASS}
                />
              )}
              {hasExternalAttachments && (
                <div
                  data-testid={`${testId}-attachments`}
                  className={`min-w-0 grid grid-cols-1 md:grid-cols-2 gap-4${hasExternalNotes ? " border-t border-[#F1F2F4] pt-3" : ""}`}
                >
                  <DocumentCard
                    fileName={
                      marineInformation.externalAttachmentsName?.trim() ||
                      t(`${LANG_BASE}.attachments`)
                    }
                    onDownload={() =>
                      handleDownload(
                        "document",
                        "bff-vessel-call",
                        marineInformation.externalAttachmentsUrl || "",
                        marineInformation.externalAttachmentsName || "",
                      )
                    }
                    onPreview={() =>
                      handlePreview(
                        "document",
                        "bff-vessel-call",
                        marineInformation.externalAttachmentsUrl || "",
                      )
                    }
                    rootClassName="font-normal"
                  />
                </div>
              )}
            </div>
          )}
          {hasInternalInformation && (
            <div
              className={`flex flex-col gap-4${hasExternalInformation ? " border-t border-[#F1F2F4] pt-3" : ""}`}
            >
              {hasInternalNotes && (
                <PaperItem
                  testId={`${testId}-internal-notes`}
                  translate={t(`${LANG_BASE}.internal-notes`)}
                  text={marineInformation.internalNotes}
                  textStyles={VALUE_CLASS}
                  tranStyle={FIELD_LABEL_CLASS}
                  wrapperStyle="min-w-0 gap-4"
                  labelClassName={FIELD_LABEL_CLASS}
                  valueClassName={VALUE_CLASS}
                />
              )}
              {hasInternalAttachments && (
                <div
                  data-testid={`${testId}-internal-attachments`}
                  className={`min-w-0 grid grid-cols-1 md:grid-cols-2 gap-4${hasInternalNotes ? " border-t border-[#F1F2F4] pt-3" : ""}`}
                >
                  <DocumentCard
                    fileName={
                      marineInformation.internalAttachmentsName?.trim() ||
                      t(`${LANG_BASE}.internal-attachments`)
                    }
                    onDownload={() =>
                      handleDownload(
                        "document",
                        "bff-vessel-call",
                        marineInformation.internalAttachmentsUrl || "",
                        marineInformation.internalAttachmentsName || "",
                      )
                    }
                    onPreview={() =>
                      handlePreview(
                        "document",
                        "bff-vessel-call",
                        marineInformation.internalAttachmentsUrl || "",
                      )
                    }
                    rootClassName="font-normal"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </Paper>
    </div>
  );
};

export default VesselCallMarineInformation;
